import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check } from "lucide-react";
import { ASSETS } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const subscribeErrorMessage = (status) => {
  if (status === 409) return "You've already reserved a spot.";
  if (status === 422) return "Enter a valid email address.";
  return "Something went wrong. Try again.";
};

const PERKS = ["10% off every bag", "Free North America shipping", "Early flavor drops", "Merch before anyone else"];

export const JoinModal = ({ open, onClose }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email, source: "coffee_club" });
      setDone(true);
      toast.success("Welcome to the Coffee Club!");
    } catch (err) {
      toast.error(subscribeErrorMessage(err?.response?.status));
    } finally {
      setLoading(false);
    }
  };

  const close = useCallback(() => {
    onClose();
    setTimeout(() => { setDone(false); setEmail(""); }, 300);
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-5 bg-void/80 backdrop-blur-md"
          onClick={close}
          role="dialog"
          aria-modal="true"
          data-testid="join-modal-overlay"
        >
          <motion.div
            initial={{ scale: 0.9, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 20, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            data-testid="join-modal"
            className="relative w-full max-w-lg bg-ash border border-ember/40 rounded-xl overflow-hidden"
          >
            <button onClick={close} data-testid="join-modal-close" className="absolute top-4 right-4 text-cream/75 hover:text-ember transition-colors z-10">
              <X size={22} />
            </button>

            <div className="p-8 md:p-10">
              {!done ? (
                <>
                  <img src={ASSETS.mascot} alt="" className="w-16 h-16 rounded-full object-cover ring-2 ring-ember mb-6" />
                  <h3 className="font-display font-black uppercase tracking-tight text-cream text-3xl md:text-4xl leading-[0.95]">
                    Join the<br /><span className="text-ember">Coffee Club</span>
                  </h3>
                  <p className="font-mono text-sm text-cream/75 mt-4">Reserve your subscription spot. We'll email you the moment it opens.</p>

                  <ul className="mt-6 space-y-2">
                    {PERKS.map((p) => (
                      <li key={p} className="flex items-center gap-3 font-mono text-sm text-cream/80">
                        <Check size={16} className="text-ember shrink-0" /> {p}
                      </li>
                    ))}
                  </ul>

                  <form onSubmit={submit} className="mt-8 flex flex-col gap-3" data-testid="club-form">
                    <input
                      type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com" data-testid="club-email-input"
                      className="w-full bg-void border border-white/15 rounded-full px-5 py-3.5 font-mono text-sm text-cream placeholder:text-cream/30 outline-none focus:border-ember transition-colors"
                    />
                    <button
                      type="submit" disabled={loading} data-testid="club-submit-button"
                      className="w-full bg-ember text-void font-mono text-xs uppercase tracking-widest py-4 rounded-full font-medium hover:bg-emberDark transition-colors disabled:opacity-50"
                    >
                      {loading ? "Reserving..." : "Reserve My Spot"}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center py-6" data-testid="club-success">
                  <div className="w-16 h-16 rounded-full bg-ember flex items-center justify-center mx-auto mb-6">
                    <Check size={32} className="text-void" />
                  </div>
                  <h3 className="font-display font-black uppercase tracking-tight text-cream text-3xl">You're on the list</h3>
                  <p className="font-mono text-sm text-cream/75 mt-3">Keep an eye on your inbox. The gremlins are getting your beans ready.</p>
                  <button onClick={close} className="mt-8 font-mono text-xs uppercase tracking-widest bg-void border border-ember/40 text-ember px-6 py-3 rounded-full hover:bg-ember hover:text-void transition-colors">
                    Back to the beans
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
